import React, { useEffect, useRef, useState } from "react";
import { Card, Button, Spinner } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import SignaturePad from "signature_pad";
import axios from "axios";
import Swal from "sweetalert2";
import { globalErrorHandler } from "../../../utils/GlobalErrorHandler";

const SignatureSection = ({ title, description, padRef, date, setDate, readOnly }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const pad = new SignaturePad(canvasRef.current, {
      backgroundColor: "#fff",
      penColor: "black",
    });
    padRef.current = pad;

    // Resize canvas for better resolution
    const resizeCanvas = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvasRef.current.width = canvasRef.current.offsetWidth * ratio;
      canvasRef.current.height = 150 * ratio;
      canvasRef.current.getContext("2d").scale(ratio, ratio);
      pad.clear();
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, [padRef]);

  return (
    <div className="mb-4">
      {title && <h5 className="fw-bold mb-2">{title}</h5>}
      {description && <p className="text-muted">{description}</p>}
      <div className="border rounded bg-light p-3">
        <canvas
          ref={canvasRef}
          className="border border-secondary rounded w-100"
          height="150"
          style={{ touchAction: "none", cursor: "crosshair" }}
        ></canvas>

        <div className="d-flex justify-content-between align-items-center mt-2">
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => padRef.current?.clear()}
          >
            Clear
          </Button>

          <div className="d-flex align-items-center">
            <label className="me-2 mb-0">Date:</label>
            <input
              type="date"
              value={date}
              readOnly={readOnly}
              onChange={(e) => setDate(e.target.value)}
              className="form-control form-control-sm w-auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const SignConsentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [guest, setGuest] = useState(null);

  const sig1 = useRef(null);
  const sig2 = useRef(null);
  const sig3 = useRef(null);

  const [date1, setDate1] = useState("");
  const [date2, setDate2] = useState("");
  const [date3, setDate3] = useState("");

  useEffect(() => {
    const fetchGuest = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_WEB_SERVICES_URL}/manager/get-guest`,
          { withCredentials: true }
        );

        if (res.data.status && res.data.guest) {
          const g = res.data.guest;
          setGuest(g);

          const today = new Date().toISOString().split("T")[0];
          setDate1(g.consent_date_1 || today);
          setDate2(g.consent_date_2 || today);
          setDate3(g.consent_date_3 || today);

       //   alert(g.consent_signature_1);
          if (g.consent_signature_1) sig1.current?.fromDataURL(g.consent_signature_1);
          if (g.consent_signature_2) sig2.current?.fromDataURL(g.consent_signature_2);
          if (g.consent_signature_3) sig3.current?.fromDataURL(g.consent_signature_3);
        }
      } catch (err) {
        globalErrorHandler(err, navigate);
      } finally {
        setLoading(false);
      }
    };

    fetchGuest();
  }, [id, navigate]);

  useEffect(() => {
  if (!guest) return;

  // Delay execution until pads are ready
  const timeout = setTimeout(() => {
    if (guest.consent_signature_1 && sig1.current)
      sig1.current.fromDataURL(guest.consent_signature_1);
    if (guest.consent_signature_2 && sig2.current)
      sig2.current.fromDataURL(guest.consent_signature_2);
    if (guest.consent_signature_3 && sig3.current)
      sig3.current.fromDataURL(guest.consent_signature_3);
  }, 500); // wait half a second for canvas init

  return () => clearTimeout(timeout);
}, [guest]);

  const handleSave = async () => {
    const getSig = (pad) => (!pad || pad.isEmpty() ? null : pad.toDataURL());
    const consent_signature_1 = getSig(sig1.current);
    const consent_signature_2 = getSig(sig2.current);
    const consent_signature_3 = getSig(sig3.current);

    if (!consent_signature_1 || !consent_signature_2 || !consent_signature_3) {
      Swal.fire("All signatures are required!", "", "warning");
      return;
    }

    setSaving(true);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_WEB_SERVICES_URL}/manager/save-sign-consent`,
        {
          consent_signature_1,
          consent_signature_2,
          consent_signature_3,
          consent_date_1: date1,
          consent_date_2: date2,
          consent_date_3: date3,
        },
        { withCredentials: true }
      );

      if (res.data.status) {
        Swal.fire("Success", res.data.message, "success");
        //navigate("/manager/guest-management/guest");
      } else {
        Swal.fire("Error", res.data.message, "error");
      }
    } catch (err) {
      globalErrorHandler(err, navigate);
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    );

  return (
    <div className="container-fluid mt-4">
      <h3 className="mb-4">Guest Consent Form</h3>
      <Card className="shadow-sm p-4 border-0">
        <SignatureSection
          title="1. Rent Responsibility Acknowledgement"
          description="By signing below I understand that failure to pay rent may result in immediate dismissal."
          padRef={sig1}
          date={date1}
          setDate={setDate1}
          readOnly={!!guest?.consent_date_1}
        />
        <hr />
        <SignatureSection
          title="2. Client Acknowledgement"
          description="I acknowledge that all provided information is true and correct."
          padRef={sig2}
          date={date2}
          setDate={setDate2}
          readOnly={!!guest?.consent_date_2}
        />
        <hr />
        <SignatureSection
          title="3. Privacy Commitment"
          description="Your privacy is protected by law."
          padRef={sig3}
          date={date3}
          setDate={setDate3}
          readOnly={!!guest?.consent_date_3}
        />

        <div className="text-end mt-4">
          <Button variant="primary" disabled={saving} onClick={handleSave}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Save Consent Form"
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default SignConsentForm;
